import { trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { GalleryDto } from '@nusign/picflow-backend-types';
import { Subject, switchMap } from 'rxjs';
import { maxHeightIfWithMarginTop } from '../../../animations/max-height.if.animation';
import { settingsMainPage, settingsSubPage } from '../../../animations/settings-page.animation';
import { DialogOptions } from '../../../interfaces/dialog-data.interface';
import { objectsMatch } from '../../../lib/objects-match';
import { PICFLOW_CONFIG } from '../../../picflow-config';
import { CheckoutService } from '../../../services/checkout.service';
import { DialogService, OnGetData } from '../../../services/dialog.service';
import { GalleryThemeService } from '../../../services/gallery-theme.service';
import { GalleryService } from '../../../services/gallery.service';
import { PermissionService } from '../../../services/permission.service';
import { ToastService } from '../../../services/toast.service';
import { UpgradeBadgeComponent } from '../../../shared-extra/components/pro-tag/upgrade-badge.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { GallerySettingsGroupComponent } from '../../components/gallery-settings-group/gallery-settings-group.component';
import { ThemeAssetPageCustomizeComponent } from '../../components/theme-asset-page-customize/theme-asset-page-customize.component';
import { ThemeAssetPageItemComponent } from '../../components/theme-asset-page-item/theme-asset-page-item.component';
import { ThemeColorsCustomizeComponent } from '../../components/theme-colors-customize/theme-colors-customize.component';
import { ThemeColorsItemComponent } from '../../components/theme-colors-item/theme-colors-item.component';
import { ThemeCoverCustomizeComponent } from '../../components/theme-cover-customize/theme-cover-customize.component';
import { ThemeCoverItemComponent } from '../../components/theme-cover-item/theme-cover-item.component';
import { ThemeLayoutCustomizeComponent } from '../../components/theme-layout-customize/theme-layout-customize.component';
import { ThemeLayoutItemComponent } from '../../components/theme-layout-item/theme-layout-item.component';
import { ThemeSelectCustomizeComponent } from '../../components/theme-select-customize/theme-select-customize.component';
import { ThemeSelectItemComponent } from '../../components/theme-select-item/theme-select-item.component';
import { ThemeTypographyCustomizeComponent } from '../../components/theme-typography-customize/theme-typography-customize.component';
import { ThemeTypographyItemComponent } from '../../components/theme-typography-item/theme-typography-item.component';
import { SaveCustomThemeDialogComponent } from '../../dialogs/save-custom-theme-dialog/save-custom-theme-dialog.component';

@Component({
  selector: 'app-gallery-theme-menu',
  imports: [
    GallerySettingsGroupComponent,
    ThemeSelectItemComponent,
    ThemeSelectCustomizeComponent,
    ThemeTypographyItemComponent,
    ThemeLayoutItemComponent,
    ThemeColorsItemComponent,
    ThemeAssetPageItemComponent,
    ThemeCoverItemComponent,
    ThemeTypographyCustomizeComponent,
    ThemeLayoutCustomizeComponent,
    ThemeColorsCustomizeComponent,
    ThemeAssetPageCustomizeComponent,
    ButtonDirective,
    ThemeCoverCustomizeComponent,
    UpgradeBadgeComponent,
    RouterLink,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('mainPage', settingsMainPage),
    trigger('subPage', settingsSubPage),
    trigger('dynamicRow', maxHeightIfWithMarginTop(40, 0, -10)),
  ],
  templateUrl: './gallery-theme-menu.component.html',
  styleUrl: './gallery-theme-menu.component.scss',
})
export class GalleryThemeMenuComponent implements OnGetData {
  dialogName = 'gallery-theme-menu';
  private galleryService = inject(GalleryService);
  private galleryThemeService = inject(GalleryThemeService);
  private permissionService = inject(PermissionService);
  private checkoutService = inject(CheckoutService);
  private dialogs = inject(DialogService);
  private config = inject(PICFLOW_CONFIG);
  private toasts = inject(ToastService);
  private galleryId = new Subject<string>();
  gallery = toSignal(this.galleryId.pipe(switchMap(galleryId => this.galleryService.getGallery(galleryId))));
  subPage = signal<'theme' | 'typography' | 'colors' | 'layout' | 'asset-page' | 'cover' | undefined>(undefined);
  isDev = this.config.env !== 'production';
  defaultOpenTypographyMenu = signal<string | undefined>(undefined);

  private customThemes = toSignal(this.galleryThemeService.getCustomThemes());
  private standardThemes = toSignal(this.galleryThemeService.getStandardThemes());

  themeIsCustom = computed(() => {
    const customthemes = this.customThemes();
    const standardThemes = this.standardThemes();
    if (!customthemes || !standardThemes) return false;
    return ![...customthemes, ...standardThemes].some(theme => objectsMatch(theme.theme, this.gallery().theme));
  });

  dialogOptions(): DialogOptions {
    return { spacingX: 20, spacingY: 24, layout: 'side', noGreyBackground: true };
  }

  onGetData(data: {
    gallery: GalleryDto;
    subPage?: 'theme' | 'typography' | 'colors' | 'layout' | 'asset-page' | 'cover';
    openTypographyMenu: string;
  }) {
    this.galleryId.next(data.gallery.id);
    if (data.subPage) this.subPage.set(data.subPage);
    this.defaultOpenTypographyMenu.set(data.openTypographyMenu);
  }

  copyJson() {
    const json = JSON.stringify(this.gallery().theme, null, 2);
    navigator.clipboard.writeText(json);
    this.toasts.add('JSON copied to clipboard', 'success');
  }
  importJson() {
    navigator.clipboard.readText().then(text => {
      try {
        const theme = JSON.parse(text);
        this.galleryService.updateGallery(this.gallery().id, { theme });
        this.toasts.add('Theme imported', 'success');
      } catch (error) {
        this.toasts.add('Invalid JSON', error);
      }
    });
  }

  saveTheme() {
    if (!this.permissionService.canDisplayElement('gallery:themes')) {
      this.checkoutService.open('gallery:themes');
      return;
    }
    const theme = this.gallery().theme;
    this.dialogs.open(SaveCustomThemeDialogComponent, { theme });
  }
}
